import { useEffect } from 'react'
import { Star, Users, PlusCircle, Share2, Gauge, Palette, Globe, Trophy, Play, Compass, Sword, GitBranch, Building, TrendingUp, Leaf, Shield, Swords, Bot, Zap, Gamepad2, Layers, Target, Brain, Heart, Volume2, Shuffle, Eye, Video, Music, Move } from 'lucide-react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Button } from '@components/ui/Button'
import { getGameBySlug, getSimilarGames, getGameUrl, getPlayUrl } from '@/data/games'
import { DownloadButton } from '@/components/game/DownloadButton'
import { applyGameManifest } from '@/hooks/usePWA'
import { SEO, GameStructuredData, BreadcrumbStructuredData } from '@/components/seo/SEO'

export function GameDetailsPage() {
  const { gameId: gameSlug } = useParams()
  const navigate = useNavigate()
  
  // Get game data from centralized store
  const game = getGameBySlug(gameSlug)
  const similarGames = getSimilarGames(game?.slug)

  // Apply game-specific manifest for PWA installation
  useEffect(() => {
    if (game && game.playable) {
      const cleanup = applyGameManifest(game)
      return cleanup
    }
  }, [game])

  // Handle game not found
  if (!game) {
    return (
      <div className="min-h-screen bg-background-dark flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-white text-4xl font-black mb-4">Game Not Found</h1>
          <p className="text-slate-400 mb-8">The game you're looking for doesn't exist.</p>
          <Link 
            to="/" 
            className="bg-game-primary text-black font-bold px-8 py-3 rounded-full"
          >
            Go Home
          </Link>
        </div>
      </div>
    )
  }

  const handlePlay = () => {
    if (game.playable) {
      navigate(getPlayUrl(game))
    }
  }
  
  const handlePlayStandalone = () => {
    if (game.playable) {
      navigate(`/standalone/${game.id}`)
    }
  }

  // Breadcrumb data for SEO
  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Games', url: '/games' },
    { name: game.title, url: `/game/${game.slug}` }
  ]

  return (
    <>
      <SEO 
        title={game.title}
        description={game.shortDescription || game.description?.substring(0, 160)}
        image={game.image}
        url={`/game/${game.slug}`}
        type="game"
        game={game}
      />
      <GameStructuredData game={game} />
      <BreadcrumbStructuredData items={breadcrumbs} />
      
      <div className="min-h-screen bg-background-dark">
        {/* Hero Section */}
        <section className="relative min-h-screen lg:h-[85vh] flex items-end overflow-hidden pt-40 lg:pt-20">
          {/* Background Image */}
          <div className="absolute inset-0 z-0">
            <img 
              alt={`${game.title} Background`}
              className="w-full h-full object-cover scale-105" 
              src={game.image}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-dark via-background-dark/40 to-transparent"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-background-dark/60 via-transparent to-background-dark/60"></div>
          </div>

          {/* Content */}
          <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-20 z-10 pb-10 lg:pb-20">
            <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-8 lg:gap-12 w-full">
              {/* Game Info */}
              <div className="max-w-full lg:max-w-[55%]">
                <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6 flex-wrap">
                  <span className="px-2 sm:px-3 py-1 bg-game-primary/20 border border-game-primary/30 text-game-primary text-[10px] font-black uppercase rounded tracking-widest">
                    {game.badge}
                  </span>
                  <span className="px-2 sm:px-3 py-1 bg-white/5 border border-white/10 text-slate-300 text-[10px] font-black uppercase rounded tracking-widest">
                  {game.genre}
                </span>
                {game.playable && (
                  <span className="px-2 sm:px-3 py-1 bg-green-500/20 border border-green-500/30 text-green-400 text-[10px] font-black uppercase rounded tracking-widest">
                    ⚡ Instant Play
                  </span>
                )}
              </div>

              <h1 className="text-white text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-black leading-[0.9] tracking-tighter mb-4 sm:mb-6">
                {game.title}
              </h1>

              <p className="text-slate-300 text-sm sm:text-lg mb-4 sm:mb-6 max-w-xl leading-relaxed whitespace-pre-line">
                {game.shortDescription}
              </p>

              <div className="flex flex-wrap items-center gap-4 sm:gap-6">
                <div className="flex items-center gap-2">
                  <Star className="text-game-primary fill-game-primary w-4 sm:w-5 h-4 sm:h-5" />
                  <span className="text-lg sm:text-xl font-bold">{game.rating}</span>
                  <span className="text-slate-500 text-xs sm:text-sm">({game.reviews} Reviews)</span>
                </div>
                <div className="h-4 w-px bg-border-dark hidden sm:block"></div>
                <div className="flex items-center gap-2 text-slate-300">
                  <Users className="w-4 h-4" />
                  <span className="font-medium text-xs sm:text-sm">{game.activePlayers} Active Now</span>
                </div>
              </div>
            </div>

            {/* Play Module */}
            <div className="bg-white/[0.03] backdrop-blur-md border border-white/10 shadow-[0_0_60px_rgba(227,255,4,0.1)] w-full lg:w-[400px] p-6 sm:p-8 rounded-2xl sm:rounded-[2.5rem] lg:-mb-10">
              <div className="flex items-center justify-between mb-8">
                <div className="flex -space-x-3">
                  <div className="w-10 h-10 rounded-full border-2 border-background-dark bg-slate-800 flex items-center justify-center text-[10px] font-bold">
                    JD
                  </div>
                  <div className="w-10 h-10 rounded-full border-2 border-background-dark bg-game-primary flex items-center justify-center text-[10px] font-bold text-black">
                    AS
                  </div>
                  <div className="w-10 h-10 rounded-full border-2 border-background-dark bg-slate-700 flex items-center justify-center text-[10px] font-bold">
                    MK
                  </div>
                </div>
                <p className="text-slate-500 text-xs">
                  <span className="text-white font-bold">{game.activePlayers}</span> playing now
                </p>
              </div>

              <button 
                onClick={handlePlay}
                disabled={!game.playable}
                className={`w-full font-black py-5 rounded-2xl text-lg flex items-center justify-center gap-3 transition-all ${
                  game.playable 
                    ? 'bg-game-primary text-black hover:scale-[1.02] hover:shadow-[0_0_30px_rgba(227,255,4,0.3)]' 
                    : 'bg-slate-700 text-slate-400 cursor-not-allowed'
                }`}
              >
                <Play className="w-6 h-6" />
                {game.playable ? 'Play Now' : 'Coming Soon'}
              </button>
              
              {/* Install as App button */}
              {game.playable && (
                <div className="mt-4">
                  <DownloadButton game={game} variant="large" />
                </div>
              )}
              <div className="flex items-center justify-center gap-6 mt-6">
                <button className="flex items-center gap-2 text-slate-400 hover:text-game-primary transition-colors">
                  <PlusCircle className="w-5 h-5" />
                  <span className="text-xs font-bold">Add to List</span>
                </button>
                <button className="flex items-center gap-2 text-slate-400 hover:text-game-primary transition-colors">
                  <Share2 className="w-5 h-5" />
                  <span className="text-xs font-bold">Share</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Details Section */}
      <section className="pt-32 pb-24 relative">
        <div className="absolute inset-0 opacity-20 pointer-events-none bg-[linear-gradient(to_right,rgba(227,255,4,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(227,255,4,0.05)_1px,transparent_1px)] bg-[size:40px_40px]"></div>
        
        <div className="w-full max-w-[1400px] mx-auto px-6 lg:px-20 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            {/* Left Column - About & Features */}
            <div className="lg:col-span-2 space-y-16">
              {/* About Experience */}
              <div>
                <h2 className="text-white text-2xl sm:text-3xl font-black mb-6 flex items-center gap-3">
                  <span className="w-2 h-8 bg-game-primary rounded-full"></span>
                  About Experience
                </h2>
                <p className="text-slate-400 text-base sm:text-lg leading-relaxed whitespace-pre-line">
                  {game.description}
                </p>
              </div>

              {/* Key Features */}
              {game.features && game.features.length > 0 && (
                <div>
                  <h2 className="text-white text-xl sm:text-2xl font-black mb-8">Key Features</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {game.features.map((feature, index) => {
                      const IconComponent = {
                        Gauge, Palette, Globe, Trophy, Compass, Sword, GitBranch, Building, 
                        TrendingUp, Leaf, Shield, Swords, Users, Bot, Zap, Gamepad2, 
                        Layers, Target, Brain, Heart, Volume2, Shuffle, Eye, Video, Music, Move
                      }[feature.icon] || Gauge
                      
                      return (
                        <div key={index} className="bg-card-dark border border-border-dark p-6 rounded-2xl flex gap-5 items-start">
                          <div className="bg-game-primary/10 p-3 rounded-xl">
                            <IconComponent className="w-7 h-7 text-game-primary" />
                          </div>
                          <div>
                            <h4 className="text-white font-bold mb-1">{feature.title}</h4>
                            <p className="text-slate-500 text-sm">{feature.description}</p>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Tech Specs & Similar Games */}
            <div className="space-y-12">
              {/* Technical Specs */}
              <div className="bg-card-dark border border-border-dark rounded-3xl p-8">
                <h3 className="text-white font-black uppercase tracking-widest text-sm mb-6">Technical Specs</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-3 border-b border-border-dark">
                    <span className="text-slate-500 text-sm">Engine</span>
                    <span className="text-game-primary font-bold text-sm bg-game-primary/10 px-3 py-1 rounded-full border border-game-primary/20 uppercase tracking-tighter">{game.engine}</span>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-border-dark">
                    <span className="text-slate-500 text-sm">FPS</span>
                    <span className="text-game-primary font-bold text-sm bg-game-primary/10 px-3 py-1 rounded-full border border-game-primary/20 uppercase tracking-tighter">{game.fps}</span>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-border-dark">
                    <span className="text-slate-500 text-sm">Platform</span>
                    <span className="text-white font-bold text-sm">Desktop / Mobile</span>
                  </div>
                  <div className="flex items-center justify-between py-3">
                    <span className="text-slate-500 text-sm">Release</span>
                    <span className="text-white font-bold text-sm">{game.releaseDate}</span>
                  </div>
                </div>
              </div>

              {/* Similar Games */}
              <div>
                <h3 className="text-white font-black text-xl mb-6">Similar Instant Games</h3>
                <div className="space-y-4">
                  {similarGames.map((similarGame) => (
                    <Link 
                      key={similarGame.id}
                      className="group flex items-center gap-4 bg-card-dark hover:bg-white/5 border border-border-dark p-3 rounded-2xl transition-all hover:shadow-[0_0_20px_rgba(227,255,4,0.15)] hover:border-game-primary" 
                      to={getGameUrl(similarGame)}
                    >
                      <img 
                        alt={similarGame.title}
                        className="w-20 h-20 object-cover rounded-xl" 
                        src={similarGame.image}
                      />
                      <div>
                        <h4 className="text-white font-bold group-hover:text-game-primary transition-colors">
                        {similarGame.title}
                      </h4>
                      <p className="text-slate-500 text-xs">{similarGame.genre}</p>
                      <div className="flex items-center gap-1 mt-1 text-game-primary">
                        <Star className="w-3 h-3 fill-game-primary" />
                        <span className="text-[10px] font-black">{similarGame.rating}</span>
                        {similarGame.playable && (
                          <span className="ml-2 text-green-400 text-[10px]">⚡ Play Now</span>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
            </div>
          </div>

          {/* Tags */}
          <div className="mt-12 pt-8 border-t border-border-dark">
            <h3 className="text-white font-bold mb-4">Tags</h3>
            <div className="flex flex-wrap gap-2">
              {game.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/genre/${tag.toLowerCase()}`}
                  className="px-4 py-2 bg-white/5 border border-border-dark rounded-full text-sm text-slate-300 hover:bg-game-primary hover:text-black hover:border-game-primary transition-all"
                >
                  {tag}
                </Link>
              ))}
            </div>
          </div>
          </div>
        </section>
      </div>
    </>
  )
}
